import type { Formatters } from '../../i18n/format'
import type { ChannelUnit, RawChannelView } from './api-types'

/**
 * How one raw telemetry row is read, ordered and filtered.
 *
 * Split from the panel component because this is where the panel is either honest
 * or not: a bitmask printed as "31" instead of "0x1F · bits 1–5", a null printed as
 * "0", or an unmapped element quietly dropped are all failures of the transparency
 * requirement (spec §6) and none of them are visible in a screenshot.
 */

/**
 * SI symbols, not translations.
 *
 * "km/h" is km/h in both languages, and putting twenty-two identical pairs in the
 * catalog would add twenty-two chances for one of them to drift while telling a
 * translator nothing. The single unit that genuinely differs — rpm / tr/min — comes
 * from the catalog through `unitSymbol`'s `translate` argument.
 */
const UNIT_SYMBOL: Partial<Record<ChannelUnit, string>> = {
  percent: '%',
  kph: 'km/h',
  metre: 'm',
  kilometre: 'km',
  litre: 'L',
  litre_per_hour: 'L/h',
  celsius: '°C',
  volt: 'V',
  ampere: 'A',
  minute: 'min',
  hour: 'h',
  millisecond: 'ms',
  g_force: 'g',
}

export function unitSymbol(
  unit: ChannelUnit | null,
  translate: (key: string) => string,
): string | null {
  if (unit === null) return null
  if (unit === 'rpm') return translate('vehicles:raw.unit.rpm')
  return UNIT_SYMBOL[unit] ?? null
}

/** `31` → `[1, 2, 3, 4, 5]`, one-based, because a wiring diagram numbers from 1. */
export function setBits(mask: number): number[] {
  if (!Number.isInteger(mask) || mask <= 0) return []
  const bits: number[] = []
  for (let bit = 0; bit < 32; bit += 1) {
    if ((mask >>> bit) & 1) bits.push(bit + 1)
  }
  return bits
}

export function formatBitmask(mask: number): string {
  const hex = `0x${(mask >>> 0).toString(16).toUpperCase().padStart(2, '0')}`
  const bits = setBits(mask)
  return bits.length === 0 ? hex : `${hex} · ${bits.join(', ')}`
}

export interface ChannelValueContext {
  formatters: Formatters
  t: (key: string) => string
}

/**
 * The display text for a row's latest value.
 *
 * Every branch that could print a bare number for something that is not a number
 * is handled: a logic channel reads "Actif"/"Inactif" rather than 1/0, a bitmask
 * reads as hex and bit ordinals rather than as a decimal that looks like a
 * measurement, an enum uses the label the registry declares, and a row with no
 * value at all says so instead of falling through to zero.
 */
export function formatChannelValue(row: RawChannelView, ctx: ChannelValueContext): string {
  const { formatters, t } = ctx

  if (row.value === null && row.text === null) return t('vehicles:raw.notReported')
  if (row.value === null) return row.text ?? t('vehicles:raw.notReported')

  if (row.unit === 'logic' || row.valueType === 'boolean') {
    return row.value === 0 ? t('vehicles:raw.logic.off') : t('vehicles:raw.logic.on')
  }

  if (row.unit === 'bitmask' || row.valueType === 'bitmask') return formatBitmask(row.value)

  if (row.enumLabel !== null) return `${row.enumLabel} (${formatters.integer(row.value)})`

  if (row.unit === 'bars') return `${formatters.integer(row.value)}/5`

  // Identifiers are printed unformatted: a cell id thousands separator turns an
  // exact identifier into something that cannot be pasted back into a tool.
  if (row.unit === 'identifier') return String(row.value)

  // An unmapped element has no declared precision. Three decimals shows the value
  // that arrived without inventing a significance it was never claimed to have.
  const digits = row.precision ?? Math.min(3, decimalsOf(row.value))
  const number = formatters.number(row.value, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })

  const symbol = unitSymbol(row.unit, t)
  return symbol === null ? number : `${number} ${symbol}`
}

function decimalsOf(value: number): number {
  if (Number.isInteger(value)) return 0
  const text = String(value)
  const dot = text.indexOf('.')
  return dot === -1 ? 0 : text.length - dot - 1
}

export const RAW_SORT_KEYS = ['label', 'value', 'category', 'ts', 'channel'] as const
export type RawSortKey = (typeof RAW_SORT_KEYS)[number]
export type SortDirection = 'asc' | 'desc'

export interface RawFilter {
  /** Matched against the channel key AND the resolved label, case-insensitively. */
  query: string
  category: string | 'all'
  availability: string | 'all'
  /** Narrows to elements the AVL map does not recognise — the `io_*` rows. */
  unmappedOnly: boolean
}

export const EMPTY_RAW_FILTER: RawFilter = {
  query: '',
  category: 'all',
  availability: 'all',
  unmappedOnly: false,
}

export function filterChannels(
  rows: readonly RawChannelView[],
  filter: RawFilter,
): RawChannelView[] {
  const needle = filter.query.trim().toLocaleLowerCase()
  return rows.filter((row) => {
    if (filter.unmappedOnly && row.known) return false
    if (filter.category !== 'all' && row.category !== filter.category) return false
    if (filter.availability !== 'all' && row.availability !== filter.availability) return false
    if (needle.length === 0) return true
    return (
      row.channel.toLocaleLowerCase().includes(needle) ||
      row.label.toLocaleLowerCase().includes(needle) ||
      row.labelFr.toLocaleLowerCase().includes(needle) ||
      row.labelEn.toLocaleLowerCase().includes(needle)
    )
  })
}

/**
 * Sorted copy. Values sort numerically with nulls last in both directions: a row
 * that reported nothing is not "the smallest reading", and floating it to the top
 * of an ascending sort would bury the extreme value the sort was opened to find.
 */
export function sortChannels(
  rows: readonly RawChannelView[],
  key: RawSortKey,
  direction: SortDirection,
  locale: string,
): RawChannelView[] {
  const sign = direction === 'asc' ? 1 : -1
  const collator = new Intl.Collator(locale, { numeric: true, sensitivity: 'base' })

  return [...rows].sort((a, b) => {
    if (key === 'value') {
      if (a.value === null && b.value === null) return collator.compare(a.label, b.label)
      if (a.value === null) return 1
      if (b.value === null) return -1
      if (a.value !== b.value) return (a.value - b.value) * sign
      return collator.compare(a.label, b.label)
    }

    if (key === 'ts') {
      const delta = Date.parse(a.ts) - Date.parse(b.ts)
      if (delta !== 0) return delta * sign
      return collator.compare(a.label, b.label)
    }

    if (key === 'category') {
      const delta = collator.compare(a.category ?? '', b.category ?? '')
      if (delta !== 0) return delta * sign
      return collator.compare(a.label, b.label)
    }

    if (key === 'channel') return collator.compare(a.channel, b.channel) * sign
    return collator.compare(a.label, b.label) * sign
  })
}
